import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { doc, onSnapshot } from 'firebase/firestore';
import { database } from '../../database/firebase';
import { setBackButtonUrl } from '../uiSlice';
import {
  restartLobbyPvpGameForPlayAgain,
  registerLobbyPlayAgainReady,
  readLobbyPlayAgainReadyUserIds,
  readLobbyPlayerStatsFromDoc,
  fetchCharacterImagesByUserIds,
  fetchLobbyUsersForCharacters,
  mergeScoreboardPlayersWithLobby,
  characterImageSrc,
  aiOpponentImageSrc,
} from '../game/useLobbyPvpFirestore';
import { readPassPlayPlayers } from '../passPlayCreate/passPlayStorage';
import {
  readScoreboardData,
  resolvePlayerDisplayName,
  clearLeaderboardNavigatedFlag,
} from './scoreboardStorage';
import {
  fetchLeaderboard,
  resolveLeaderboardGameMode,
  resolveLeaderboardGameModeFromStorage,
} from '../../functions/fetchLeaderboard';
import {
  clearPlayAgainArm,
  resetGameStoreForPlayAgain,
} from './prepareNextGameAfterScoreboard';
import {
  applyThemeCssVariables,
  buildThemeActionButtonStyle,
  resolveThemeColors,
} from '../../utils/resolveThemeColors';
import './leaderboard.css';

const CURRENT_LOBBY_KEY = 'mythFactCurrentLobby';

function PersonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" />
    </svg>
  );
}

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem('userData') ||
      sessionStorage.getItem('userData') ||
      '{}';
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function readScoreboardSnapshot(locationState) {
  if (locationState?.scoreboard?.players?.length) {
    return locationState.scoreboard;
  }
  return readScoreboardData();
}

function resolveLobbyName(userData, locationState) {
  const fromState =
    typeof locationState?.lobbyName === 'string'
      ? locationState.lobbyName.trim()
      : '';
  if (fromState) return fromState;
  try {
    const fromSession = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (fromSession?.trim()) return fromSession.trim();
  } catch {
    /* ignore */
  }
  return String(
    userData?.lobby ?? userData?.lobbyName ?? userData?.lobby_name ?? ''
  ).trim();
}

function normalizePlayers(raw, storedUser) {
  if (!Array.isArray(raw)) return [];
  const myUserId = String(
    storedUser?.userId ?? storedUser?.userid ?? storedUser?.id ?? ''
  ).trim();
  return raw
    .map((p) => {
      const uid = String(
        p?.userId ?? p?.userid ?? p?.id ?? p?.playerId ?? ''
      ).trim();
      const record =
        myUserId && uid && uid === myUserId ? { ...storedUser, ...p } : p;
      return {
        userId: uid,
        name: resolvePlayerDisplayName(record),
        score: Number.isFinite(Number(p?.score)) ? Number(p.score) : 0,
      };
    })
    .slice(0, 2);
}

function resolveGameMode(scoreboard) {
  if (scoreboard?.categoryMode) {
    return resolveLeaderboardGameMode(scoreboard.categoryMode);
  }
  if (scoreboard?.isLobbyPvp) return 'pvp';
  if (scoreboard?.isPassPlay) return 'offline';
  return resolveLeaderboardGameModeFromStorage();
}

export default function Leaderboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // const themeData = useSelector((state) => state.theme?.data);
  const themeStatus = useSelector((state) => state.theme?.status);
  // const themeColors = useMemo(() => resolveThemeColors(themeData), [themeData]);
  const { data: themeData } = useSelector((state) => state.theme);
  const themeColors = themeData?.colors || themeData || {};
  const optionBgColor =
    typeof themeColors.option_color === "string" && themeColors.option_color.trim()
      ? themeColors.option_color
      : "#2a4a7a";

  const optionTextColor =
    typeof themeColors.option_text_color === "string" &&
    themeColors.option_text_color.trim()
      ? themeColors.option_text_color
      : "#ffffff";

  const textStyle = themeColors?.text_color
    ? { color: themeColors.text_color }
    : undefined;
    const actionButtonStyle = {
      backgroundColor: optionBgColor,
      color: optionTextColor,
    };

  useEffect(() => {
    if (themeStatus !== 'succeeded' || !themeData) return;
    applyThemeCssVariables(themeData);
  }, [themeData, themeStatus]);

  const [scoreboard] = useState(() => readScoreboardSnapshot(location.state));

  const storedUser = useMemo(() => readStoredUserData(), [location.key]);

  const gameMode = useMemo(() => resolveGameMode(scoreboard), [scoreboard]);

  const isLobbyPvp = gameMode === 'pvp';

  const lobby = useMemo(
    () => resolveLobbyName(storedUser, location.state),
    [storedUser, location.state]
  );

  const fallbackPlayers = useMemo(
    () => normalizePlayers(scoreboard?.players, storedUser),
    [scoreboard?.players, storedUser]
  );

  const [players, setPlayers] = useState(fallbackPlayers);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [playAgainReadyCount, setPlayAgainReadyCount] = useState(0);
  const [playAgainReadyUserIds, setPlayAgainReadyUserIds] = useState([]);
  const [playAgainSubmitting, setPlayAgainSubmitting] = useState(false);
  const [characterImageByUserId, setCharacterImageByUserId] = useState({});
  const [characterImageByName, setCharacterImageByName] = useState({});

  const sessionId = String(storedUser?.sessionId ?? '').trim();
  const organizationId = String(storedUser?.organizationId ?? '').trim();
  const userId = String(
    storedUser?.userId ?? storedUser?.userid ?? storedUser?.id ?? ''
  ).trim();
  const token = String(storedUser?.token ?? '').trim();

  const hasUserClickedPlayAgain =
    isLobbyPvp &&
    Boolean(userId) &&
    playAgainReadyUserIds.includes(userId);

  const loadSeqRef = useRef(0);
  const lobbyStatusRef = useRef('');
  const initialLobbySnapshotRef = useRef(true);
  const apiFallbackFetchedRef = useRef(false);

  const loadLeaderboardFromApi = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    const sessionFallback = fallbackPlayers;

    if (!sessionId || !organizationId) {
      setPlayers(sessionFallback);
      setLoading(false);
      return;
    }

    if (gameMode !== 'pvp' && !userId) {
      setPlayers(sessionFallback);
      setLoading(false);
      return;
    }

    setLoading(true);
    setFetchError(null);

    const params = {
      gameMode,
      sessionId,
      organizationId,
      userId,
      token,
      lobby: gameMode === 'pvp' ? lobby : undefined,
      limit: 2,
    };

    try {
      const data = await fetchLeaderboard(params);
      if (seq !== loadSeqRef.current) return;
      const fromApi = normalizePlayers(data.players, storedUser);
      setPlayers(
        fromApi.length ? fromApi : sessionFallback.length ? sessionFallback : fromApi
      );
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      setFetchError(err.message || 'Failed to load scoreboard');
      if (sessionFallback.length) {
        setPlayers(sessionFallback);
      }
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
      }
    }
  }, [
    fallbackPlayers,
    gameMode,
    lobby,
    organizationId,
    sessionId,
    storedUser,
    token,
    userId,
  ]);

  const fetchPvpApiFallbackOnce = useCallback(async () => {
    if (apiFallbackFetchedRef.current || !sessionId || !organizationId) return;
    apiFallbackFetchedRef.current = true;
    try {
      const data = await fetchLeaderboard({
        gameMode: 'pvp',
        sessionId,
        organizationId,
        userId,
        token,
        lobby,
        limit: 2,
      });
      const fromApi = normalizePlayers(data.players, storedUser);
      if (fromApi.length) {
        setPlayers(fromApi);
      }
      setLoading(false);
      setFetchError(null);
    } catch (err) {
      setFetchError(err.message || 'Failed to load scoreboard');
      setLoading(false);
    }
  }, [lobby, organizationId, sessionId, storedUser, token, userId]);

  useEffect(() => {
    if (isLobbyPvp) return undefined;
    setPlayers(fallbackPlayers);
    void loadLeaderboardFromApi();
  }, [isLobbyPvp, loadLeaderboardFromApi, fallbackPlayers]);

  useEffect(() => {
    if (!isLobbyPvp || !sessionId || !lobby) {
      if (isLobbyPvp) {
        setLoading(false);
      }
      return undefined;
    }

    apiFallbackFetchedRef.current = false;
    setLoading(true);
    setFetchError(null);

    const gameDocRef = doc(database, `lobbies/${sessionId}/lobby/${lobby}`);
    let active = true;

    const unsubscribe = onSnapshot(
      gameDocRef,
      (snap) => {
        if (!active || !snap.exists()) return;
        const data = snap.data() || {};
        const status = String(data.status ?? '').trim().toLowerCase();
        const prevStatus = lobbyStatusRef.current;

        const statsPlayers = normalizePlayers(
          readLobbyPlayerStatsFromDoc(data),
          storedUser
        );
        if (statsPlayers.length) {
          setPlayers(statsPlayers);
          setLoading(false);
        } else if (!apiFallbackFetchedRef.current) {
          void fetchPvpApiFallbackOnce();
        }

        if (status === 'gameover') {
          const readyIds = readLobbyPlayAgainReadyUserIds(data);
          setPlayAgainReadyUserIds(readyIds);
          setPlayAgainReadyCount(readyIds.length);
        }

        if (initialLobbySnapshotRef.current) {
          initialLobbySnapshotRef.current = false;
          lobbyStatusRef.current = status;
          return;
        }

        if (status === 'play' && prevStatus === 'gameover') {
          try {
            sessionStorage.setItem('gameMode', 'pvp');
          } catch {
            /* ignore */
          }
          clearLeaderboardNavigatedFlag();
          resetGameStoreForPlayAgain('pvp', { isLobbyPvp: true });
          navigate('/game', { replace: true });
        }

        lobbyStatusRef.current = status;
      },
      (err) => {
        if (active) {
          console.error('Leaderboard lobby snapshot failed:', err);
          void fetchPvpApiFallbackOnce();
        }
      }
    );

    return () => {
      active = false;
      initialLobbySnapshotRef.current = true;
    };
  }, [
    isLobbyPvp,
    sessionId,
    lobby,
    navigate,
    storedUser,
    fetchPvpApiFallbackOnce,
  ]);

  useEffect(() => {
    dispatch(setBackButtonUrl('/category'));
  }, [dispatch]);

  useEffect(() => {
    if (!sessionId) {
      setCharacterImageByUserId({});
      setCharacterImageByName({});
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        if (isLobbyPvp && lobby) {
          const lobbyUsers = await fetchLobbyUsersForCharacters(sessionId, lobby);
          const merged = mergeScoreboardPlayersWithLobby(players, lobbyUsers);
          const allIds = [
            ...new Set([
              ...merged.map((p) => p.userId).filter(Boolean),
              ...lobbyUsers.map((u) => u.userId),
            ]),
          ];

          const byUserId = await fetchCharacterImagesByUserIds(
            sessionId,
            lobby,
            allIds
          );

          const byName = {};
          lobbyUsers.forEach((u) => {
            const src = byUserId[u.userId];
            const key = String(u.name ?? '').trim().toLowerCase();
            if (src && key) byName[key] = src;
          });
          merged.forEach((p) => {
            const src = byUserId[p.userId];
            const key = String(p.name ?? '').trim().toLowerCase();
            if (src && key && !byName[key]) byName[key] = src;
          });

          if (!cancelled) {
            setCharacterImageByUserId(byUserId);
            setCharacterImageByName(byName);
            setPlayers((prev) => {
              const next = mergeScoreboardPlayersWithLobby(prev, lobbyUsers);
              const same =
                prev.length === next.length &&
                prev.every(
                  (p, i) =>
                    p.userId === next[i]?.userId &&
                    p.name === next[i]?.name &&
                    p.score === next[i]?.score
                );
              return same ? prev : next;
            });
          }
          return;
        }

        if (gameMode === 'offline') {
          const pass = readPassPlayPlayers();
          const byName = {};
          if (pass?.xName) {
            const src = characterImageSrc(pass.xCharacter);
            if (src) byName[String(pass.xName).trim().toLowerCase()] = src;
          }
          if (pass?.oName) {
            const src = characterImageSrc(pass.oCharacter);
            if (src) byName[String(pass.oName).trim().toLowerCase()] = src;
          }
          if (!cancelled) {
            setCharacterImageByUserId({});
            setCharacterImageByName(byName);
          }
          return;
        }

        if (gameMode === 'ai') {
          const computerSrc = aiOpponentImageSrc();
          const byName = {};
          const byUserId = {};

          if (computerSrc) {
            byUserId.computer = computerSrc;
            byName.computer = computerSrc;
          }

          const humanIds = [
            ...new Set(
              [
                userId,
                ...players
                  .map((p) => p.userId)
                  .filter((id) => id && String(id).toLowerCase() !== 'computer'),
              ].filter(Boolean)
            ),
          ];

          if (humanIds.length) {
            const humanImages = await fetchCharacterImagesByUserIds(
              sessionId,
              '',
              humanIds
            );
            Object.assign(byUserId, humanImages);
            players.forEach((p) => {
              if (String(p.userId ?? '').toLowerCase() === 'computer') return;
              const src = humanImages[p.userId];
              const key = String(p.name ?? '').trim().toLowerCase();
              if (src && key) byName[key] = src;
            });
            const selfName = resolvePlayerDisplayName(storedUser).toLowerCase();
            if (selfName && byUserId[userId] && !byName[selfName]) {
              byName[selfName] = byUserId[userId];
            }
          }

          if (!cancelled) {
            setCharacterImageByUserId(byUserId);
            setCharacterImageByName(byName);
          }
          return;
        }

        const ids = players.map((p) => p.userId).filter(Boolean);
        if (!ids.length) {
          if (!cancelled) {
            setCharacterImageByUserId({});
            setCharacterImageByName({});
          }
          return;
        }

        const byUserId = await fetchCharacterImagesByUserIds(
          sessionId,
          '',
          ids
        );
        if (!cancelled) {
          setCharacterImageByUserId(byUserId);
          setCharacterImageByName({});
        }
      } catch (err) {
        console.warn('Leaderboard character images failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, lobby, isLobbyPvp, gameMode, players, userId, storedUser]);

  const handleHome = () => {
    navigate('/category');
  };

  const handlePlayAgain = async () => {
    const mode = scoreboard?.categoryMode || (isLobbyPvp ? 'pvp' : '');
    if (mode) {
      try {
        sessionStorage.setItem('gameMode', mode);
      } catch {
        /* ignore */
      }
    }

    if (isLobbyPvp && sessionId && lobby && userId) {
      if (playAgainSubmitting || hasUserClickedPlayAgain) return;
      setPlayAgainSubmitting(true);
      try {
        const { ok, readyCount, bothReady } = await registerLobbyPlayAgainReady(
          sessionId,
          lobby,
          userId
        );
        if (!ok) {
          clearPlayAgainArm();
          return;
        }
        setPlayAgainReadyCount(readyCount);
        setPlayAgainReadyUserIds((prev) =>
          prev.includes(userId) ? prev : [...prev, userId]
        );

        if (!bothReady) {
          return;
        }

        clearLeaderboardNavigatedFlag();
        resetGameStoreForPlayAgain(mode, { isLobbyPvp });

        const restarted = await restartLobbyPvpGameForPlayAgain(sessionId, lobby);
        if (!restarted) {
          clearPlayAgainArm();
          return;
        }
        navigate('/game', { replace: true });
      } finally {
        setPlayAgainSubmitting(false);
      }
      return;
    }

    clearLeaderboardNavigatedFlag();
    resetGameStoreForPlayAgain(mode, { isLobbyPvp });
    navigate('/game', { replace: true });
  };

  if (!loading && !players.length && !fallbackPlayers.length) {
    if (lobby || sessionId) {
      return (
        <section className="scoreboard-page" aria-label="Scoreboard">
          <div className="scoreboard-grid scoreboard-grid--top" aria-hidden="true" />
          <div className="scoreboard-grid scoreboard-grid--bottom" aria-hidden="true" />
          <button type="button" className="scoreboard-home" onClick={handleHome}>
            <span className="scoreboard-home__icon" aria-hidden>
              ⌂
            </span>
            Home
          </button>
          <p className="scoreboard-empty" style={textStyle}>
            {fetchError || 'No scores yet. Play a game to see the scoreboard.'}
          </p>
        </section>
      );
    }
  }

  const displayPlayers =
    players.length >= 2
      ? players
      : gameMode === 'ai'
        ? [
            players[0] || {
              name: resolvePlayerDisplayName(storedUser) || 'Player',
              score: 0,
              userId,
            },
            { name: 'Computer', score: 0, userId: 'computer' },
          ]
        : [
            players[0] || { name: 'Player', score: 0 },
            { name: 'Opponent', score: 0 },
          ];

  return (
    <section className="scoreboard-page" aria-label="Scoreboard">
      <div className="scoreboard-grid scoreboard-grid--top" aria-hidden="true" />
      <div className="scoreboard-grid scoreboard-grid--bottom" aria-hidden="true" />

      <button type="button" className="scoreboard-home" onClick={handleHome}>
        <span className="scoreboard-home__icon" aria-hidden>
          ⌂
        </span>
        Home
      </button>

      <div className="scoreboard-content">
        <header className="scoreboard-header">
          <span className="scoreboard-trophy" aria-hidden>
            🏆
          </span>
          <h1 className="scoreboard-title" style={textStyle}>
            Scoreboard
          </h1>
          <span className="scoreboard-trophy" aria-hidden>
            🏆
          </span>
        </header>

        {loading && !players.length ? (
          <p className="scoreboard-empty" style={textStyle}>
            Loading scores…
          </p>
        ) : (
          <div className="scoreboard-players">
            {displayPlayers.map((player, index) => {
              const nameKey = String(player.name ?? '').trim().toLowerCase();
              const avatarSrc =
                (player.userId && characterImageByUserId[player.userId]) ||
                (nameKey && characterImageByName[nameKey]) ||
                null;
              return (
              <div
                key={player.userId || `${player.name}-${index}`}
                className="scoreboard-player"
              >
                <div className="scoreboard-avatar-box">
                  {avatarSrc ? (
                    <img
                      src={avatarSrc}
                      alt=""
                      className="scoreboard-avatar-img"
                    />
                  ) : (
                    <span className="scoreboard-avatar-icon">
                      <PersonIcon />
                    </span>
                  )}
                </div>
                <div className="scoreboard-name" style={textStyle}>
                  {player.name}
                </div>
                <div className="scoreboard-score-box" style={textStyle}>
                  {player.score}
                </div>
              </div>
            );
            })}
          </div>
        )}

        <div className="scoreboard-actions">
          <button
            type="button"
            className="scoreboard-play-again"
            style={actionButtonStyle}
            onClick={handlePlayAgain}
            disabled={playAgainSubmitting || hasUserClickedPlayAgain}
            aria-label={
              hasUserClickedPlayAgain
                ? 'Play Again — waiting for opponent'
                : 'Play Again'
            }
          >
            Play Again
            {isLobbyPvp ? (
              <span className="scoreboard-play-again__count" aria-live="polite">
                {' '}
                ({playAgainReadyCount}/2)
              </span>
            ) : null}
          </button>
        </div>
      </div>
    </section>
  );
}
